import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldAlert, RefreshCw, Check, AlertTriangle } from "lucide-react";

const SEV_CHIP = { critical: "chip-amber", high: "chip-amber", med: "chip-blue", low: "chip-gray" };
const SEV_COLOR = { critical: "#DC2626", high: "#B45309", med: "#4060FF", low: "#6B7280" };

export default function RiskAlerts() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("open");
  const [scanning, setScanning] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await api.get("/risk-alerts", { params });
      setAlerts(data);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // Demo accounts skip the Risk fetch entirely — the early-return below sends them elsewhere.
    if (user?.is_demo) return;
    fetchAll();
  }, [filter, user?.is_demo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Demo accounts don't get Risk alerts — bounce them straight back to the dashboard.
  // (After every hook is registered, so the hook order stays stable across renders.)
  if (user?.is_demo) return <Navigate to="/dashboard" replace />;

  const scan = async () => {
    setScanning(true);
    try { const { data } = await api.post("/risk-alerts/scan"); await fetchAll(); alert(`Scan complete. ${data.created} new alerts created.`); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setScanning(false); }
  };

  const resolve = async (id) => {
    try { await api.patch(`/risk-alerts/${id}/resolve`); fetchAll(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const open = alerts.filter((a) => a.status === "open").length;

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10">

        <div className="mb-8 fade-up flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="pill mb-3"><span className="dot" />Risk guardrails</div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">Risk alerts</h1>
            <p className="text-ink-2 mt-2 text-sm">Automated alerts for missing risk disclosures, zero recovery, high fixed-staking concentration, failed withdrawals, and high slippage.</p>
          </div>
          <div className="flex items-center gap-2">
            {user?.role !== "participant" && (
              <button onClick={scan} disabled={scanning} className="btn-secondary" data-testid="scan-btn"><RefreshCw size={14} /> {scanning ? "Scanning…" : "Run scan"}</button>
            )}
            <button onClick={fetchAll} className="btn-ghost" data-testid="refresh-btn">Refresh</button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="kpi-tile" data-testid="kpi-open"><div className="kpi-label">Open</div><div className="kpi-num" style={{ color: open > 0 ? "#DC2626" : "#00863A" }}>{open}</div></div>
          <div className="kpi-tile" data-testid="kpi-total"><div className="kpi-label">Total alerts</div><div className="kpi-num">{alerts.length}</div></div>
          <div className="kpi-tile" data-testid="kpi-high"><div className="kpi-label">High severity</div><div className="kpi-num">{alerts.filter(a => a.severity === "high" || a.severity === "critical").length}</div></div>
          <div className="kpi-tile" data-testid="kpi-resolved"><div className="kpi-label">Resolved</div><div className="kpi-num">{alerts.filter(a => a.status === "resolved").length}</div></div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {["open", "resolved", "all"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${filter === s ? "text-white" : "bg-white/60 text-ink-2 hover:bg-white"}`} style={filter === s ? { background: "linear-gradient(135deg, #4060FF, #6B8AFF)" } : {}} data-testid={`filter-${s}`}>
              <span className="uppercase tracking-wide">{s}</span>
            </button>
          ))}
        </div>

        <div className="glass overflow-x-auto">
          <table className="modern" data-testid="alerts-table">
            <thead><tr><th>When</th><th>Severity</th><th>Type</th><th>Message</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan="6" className="text-center py-8 text-ink-3">Loading…</td></tr>
              : alerts.length === 0 ? <tr><td colSpan="6" className="text-center py-10" data-testid="alerts-empty">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(0,200,83,0.16)" }}><Check size={20} style={{ color: "#00863A" }} /></div>
                  <div className="text-sm text-ink-2">No {filter !== "all" ? filter : ""} alerts. You're all clear.</div>
                </div>
              </td></tr>
              : alerts.map((a) => (
                <tr key={a.id} data-testid={`alert-row-${a.id}`}>
                  <td className="num text-xs">{a.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td><span className={`chip ${SEV_CHIP[a.severity] || "chip-gray"}`}>
                    <ShieldAlert size={11} style={{ color: SEV_COLOR[a.severity] }} /> {a.severity}
                  </span></td>
                  <td className="font-mono text-xs">{a.alert_type}</td>
                  <td className="text-sm">{a.message}</td>
                  <td><span className={`chip ${a.status === "open" ? "chip-amber" : "chip-green"}`}>{a.status}</span></td>
                  <td>{a.status === "open" && user?.role !== "participant" && <button onClick={() => resolve(a.id)} className="btn-ghost border border-black/10" style={{ background: "rgba(255,255,255,0.6)" }} data-testid={`resolve-${a.id}`}><Check size={12} /> Resolve</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 glass p-5 flex items-start gap-3" style={{ background: "rgba(64,96,255,0.05)" }}>
          <AlertTriangle size={18} style={{ color: "#4060FF" }} />
          <div className="text-sm text-ink-2">
            <b>How scanning works:</b> Running a scan checks every participant against 3 baseline rules (risk disclosure accepted, USDT recovered &gt; 0 if deployed, fixed-staking concentration ≤ 70%). High-slippage and failed-withdrawal alerts are created automatically when you log a withdrawal.
          </div>
        </div>

        {error && <div className="mt-4 chip chip-amber" data-testid="risk-error">{error}</div>}
      </div>
      <HelpFab />
    </div>
  );
}
